/* Quas stockist locator bootstrap.
   Progressive enhancement over the server-rendered metaobject list.
   Owns: filters, venue text search, UK postcode lookup (api.postcodes.io),
   browser geolocation, client-side distance sorting, selection state, and the
    event contract with the deferred Google Maps provider module
    (assets/quas-stockist-map.js), which mounts against [data-quas-stockist-map]. */

/* Shopify asset revision: stockist feedback runtime refresh. */

(() => {
  const FULL_POSTCODE = /^[A-Za-z]{1,2}\d[A-Za-z\d]?\s*\d[A-Za-z]{2}$/;
  const OUTWARD_POSTCODE = /^[A-Za-z]{1,2}\d[A-Za-z\d]?$/;
  const LOCATE_TIMEOUT_MS = 8000;
  const FETCH_TIMEOUT_MS = 8000;
  const PENDING_INDICATOR_MS = 300;
  const COLLAPSED_COUNT = 8;
  const LINKED_CATEGORIES = new Set(['all', 'shop', 'food_drink', 'wellness', 'event_partner']);

  const plural = (count, singular, pluralForm) => `${count} ${count === 1 ? singular : pluralForm}`;

  const haversineMiles = (lat1, lng1, lat2, lng2) => {
    const rad = Math.PI / 180;
    const dLat = (lat2 - lat1) * rad;
    const dLng = (lng2 - lng1) * rad;
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1 * rad) * Math.cos(lat2 * rad) * Math.sin(dLng / 2) * Math.sin(dLng / 2);
    return 3958.8 * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  };

  const init = (root) => {
    const dataEl = root.querySelector('script[type="application/json"][id^="quas-stockist-data-"]');
    const listEl = root.querySelector('.quas-stockists__list');
    if (!dataEl || !listEl) return;

    let dataset;
    try {
      dataset = JSON.parse(dataEl.textContent);
    } catch (error) {
      return;
    }
    if (!dataset || !Array.isArray(dataset.venues)) return;

    const venues = dataset.venues;
    const events = Array.isArray(dataset.events) ? dataset.events : [];
    const labels = dataset.labels || {};
    const messages = dataset.messages || {};

    const searchForm = root.querySelector('[data-stockist-search]');
    const input = root.querySelector('.quas-stockists__search-input');
    const clearBtn = root.querySelector('[data-stockist-clear]');
    const locateBtn = root.querySelector('[data-stockist-locate]');
    const filtersWrap = root.querySelector('.quas-stockists__filters');
    const statusEl = root.querySelector('[data-stockist-status]');
    const emptyEl = root.querySelector('[data-stockist-empty]');
    const alertEl = root.querySelector('[data-stockist-alert]');
    const mapShell = root.querySelector('[data-quas-stockist-map]');
    const eventSection = root.querySelector('[data-stockist-events]');
    const eventListEl = eventSection ? eventSection.querySelector('.quas-stockists__events-list') : null;
    const resultsEl = root.querySelector('.quas-stockists__results');
    const moreBtn = root.querySelector('[data-stockist-more]');

    const listItems = Array.from(listEl.children).filter((item) =>
      item.classList.contains('quas-stockists__item')
    );
    const cards = listItems
      .map((item) => item.querySelector('[data-quas-stockist]'))
      .filter(Boolean);
    const cardByHandle = new Map();
    const itemByHandle = new Map();
    cards.forEach((card) => {
      const handle = card.getAttribute('data-handle');
      if (handle) {
        cardByHandle.set(handle, card);
        itemByHandle.set(handle, card.closest('.quas-stockists__item'));
      }
    });
    const originalOrder = listItems.slice();
    // Moving all the cards costs a phone a long frame, so the list is only
    // reordered when the visible order actually changes.
    const placeInOrder = (items) => {
      const wanted = new Set(items);
      const current = Array.from(listEl.children).filter((child) => wanted.has(child));
      if (current.length === items.length && current.every((child, index) => child === items[index])) return;
      items.forEach((item) => listEl.appendChild(item));
    };
    const eventListItems = eventListEl
      ? Array.from(eventListEl.children).filter((item) => item.classList.contains('quas-stockists__item'))
      : [];
    const eventCards = eventListItems
      .map((item) => item.querySelector('[data-quas-stockist]'))
      .filter(Boolean);
    const eventItemByHandle = new Map();
    eventCards.forEach((card) => {
      const handle = card.getAttribute('data-handle');
      if (handle) eventItemByHandle.set(handle, card.closest('.quas-stockists__item'));
    });

    const state = { filter: 'all', query: '', origin: null, notFound: false, expanded: false };
    let searchSequence = 0;
    let alertReturnFocus = null;
    let selectedHandle = null;

    const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const scrollTo = (el) => {
      el.scrollIntoView({ behavior: reducedMotion() ? 'auto' : 'smooth', block: 'start' });
    };

    // On wide screens with a map the list scrolls inside its own panel.
    const listScrolls = () =>
      Boolean(resultsEl) && window.getComputedStyle(resultsEl).overflowY === 'auto';

    const inViewport = (el) => {
      const rect = el.getBoundingClientRect();
      return rect.top >= 0 && rect.bottom <= window.innerHeight;
    };

    // Expands the collapsed list in place, without re-fitting the map.
    const expandList = () => {
      state.expanded = true;
      listEl.querySelectorAll('.quas-stockists__item.is-beyond').forEach((item) => {
        item.classList.remove('is-beyond');
      });
      if (moreBtn) moreBtn.hidden = true;
    };

    const revealItem = (item) => {
      if (!item) return;
      if (listScrolls()) {
        resultsEl.scrollTo({
          top: Math.max(0, item.offsetTop - 8),
          behavior: reducedMotion() ? 'auto' : 'smooth',
        });
        return;
      }
      scrollTo(item);
    };

    const hideAlert = () => {
      if (!alertEl) return;
      alertEl.hidden = true;
      alertEl.textContent = '';
      alertEl.removeAttribute('role');
      if (alertReturnFocus) {
        alertReturnFocus.focus();
        alertReturnFocus = null;
      }
    };

    const showAlert = (message, returnFocusEl) => {
      if (!alertEl) return;
      alertReturnFocus = returnFocusEl || null;
      alertEl.textContent = '';
      const text = document.createElement('p');
      text.textContent = message;
      const dismiss = document.createElement('button');
      dismiss.type = 'button';
      dismiss.textContent = messages.dismiss || 'Dismiss';
      dismiss.addEventListener('click', hideAlert);
      alertEl.append(text, dismiss);
      alertEl.setAttribute('role', 'alert');
      alertEl.hidden = false;
    };

    const matchesQuery = (venue, query) => {
      const haystack = [venue.name, venue.city, venue.address, labels[venue.category]]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return query
        .toLowerCase()
        .split(/\s+/)
        .every((token) => haystack.includes(token));
    };

    const statusText = (count) => {
      if (state.filter === 'event_partner') {
        return plural(count, 'event partner', 'event partners') + ' shown';
      }
      if (state.origin) {
        return plural(count, 'place', 'places') + ` near ${state.origin.label}`;
      }
      return plural(count, 'place', 'places') + ' shown';
    };

    const setDistance = (card, miles) => {
      const badge = card.querySelector('[data-stockist-distance]');
      if (!badge) return;
      const text = badge.querySelector('[data-stockist-distance-text]');
      const sr = badge.querySelector('[data-stockist-distance-sr]');
      if (miles == null) {
        badge.hidden = true;
        return;
      }
      const rounded = miles < 10 ? miles.toFixed(1) : String(Math.round(miles));
      if (text) text.textContent = `${rounded} mi`;
      if (sr) sr.textContent = `${rounded} ${rounded === '1.0' || rounded === '1' ? 'mile' : 'miles'} away`;
      badge.hidden = false;
    };

    // Town headings mark the first visible entry of each run of the same town.
    // Distance sorting interleaves towns, so every entry then shows its own town.
    const markGroups = () => {
      const grouped = !state.origin;
      listEl.dataset.grouped = grouped ? 'true' : 'false';
      let previousCity = null;
      Array.from(listEl.children).forEach((item) => {
        if (!item.classList.contains('quas-stockists__item')) return;
        if (item.hidden) {
          item.classList.remove('is-group-start');
          return;
        }
        const city = item.getAttribute('data-city') || '';
        item.classList.toggle('is-group-start', grouped && city !== previousCity);
        previousCity = city;
      });
    };

    const apply = () => {
      const eventsMode = state.filter === 'event_partner';
      const isDefault = state.filter === 'all' && !state.query && !state.origin;
      let visibleHandles = null;
      let eventVisibleHandles = null;
      let matching = [];

      if (eventsMode) {
        matching = events.filter((event) => !state.query || matchesQuery(event, state.query));
        eventVisibleHandles = new Set(matching.map((event) => event.handle));
        listEl.hidden = true;
        if (eventSection) {
          eventSection.hidden = false;
          eventSection.dataset.active = 'true';
        }
        eventCards.forEach((card) => {
          const handle = card.getAttribute('data-handle');
          const item = eventItemByHandle.get(handle);
          if (item) item.hidden = !eventVisibleHandles.has(handle);
        });
        cards.forEach((card) => setDistance(card, null));
      } else if (isDefault) {
        placeInOrder(originalOrder);
        cards.forEach((card) => {
          const item = itemByHandle.get(card.getAttribute('data-handle'));
          if (item) item.hidden = false;
          setDistance(card, null);
        });
        listEl.hidden = false;
        if (eventSection) {
          eventSection.hidden = false;
          eventSection.dataset.active = 'false';
          eventListItems.forEach((item) => {
            item.hidden = false;
          });
        }
      } else {
        matching = venues.filter((venue) => {
          if (state.filter !== 'all' && venue.category !== state.filter) return false;
          if (state.query && !matchesQuery(venue, state.query)) return false;
          return true;
        });

        if (state.origin) {
          matching.forEach((venue) => {
            venue._distance =
              typeof venue.latitude === 'number' && typeof venue.longitude === 'number'
                ? haversineMiles(state.origin.lat, state.origin.lng, venue.latitude, venue.longitude)
                : null;
          });
          matching.sort((a, b) => {
            if (a._distance == null && b._distance == null) return 0;
            if (a._distance == null) return 1;
            if (b._distance == null) return -1;
            return a._distance - b._distance;
          });
        }

        visibleHandles = new Set(matching.map((venue) => venue.handle));
        // Distance search reorders by proximity; otherwise keep the server's town order.
        if (state.origin) {
          placeInOrder(matching.map((venue) => itemByHandle.get(venue.handle)).filter(Boolean));
        } else {
          placeInOrder(originalOrder);
        }
        cards.forEach((card) => {
          const handle = card.getAttribute('data-handle');
          const visible = visibleHandles.has(handle);
          const item = itemByHandle.get(handle);
          if (item) item.hidden = !visible;
          if (!visible) return;
          if (state.origin) {
            const venue = matching.find((item) => item.handle === handle);
            setDistance(card, venue && venue._distance != null ? venue._distance : null);
          } else {
            setDistance(card, null);
          }
        });
        listEl.hidden = false;
        if (eventSection) {
          eventSection.hidden = true;
          eventSection.dataset.active = 'false';
        }
      }

      markGroups();

      // Long lists open collapsed; CSS applies the cut only where the list does not scroll.
      let visibleIndex = 0;
      Array.from(listEl.children).forEach((item) => {
        if (!item.classList.contains('quas-stockists__item')) return;
        const beyond = !item.hidden && !state.expanded && visibleIndex >= COLLAPSED_COUNT;
        if (!item.hidden) visibleIndex += 1;
        item.classList.toggle('is-beyond', beyond);
      });
      if (moreBtn) {
        const template = moreBtn.getAttribute('data-label') || 'Show all [count] places';
        moreBtn.textContent = template.replace('[count]', String(visibleIndex));
        moreBtn.hidden = eventsMode || state.expanded || visibleIndex <= COLLAPSED_COUNT;
      }
      root.dataset.mode = eventsMode ? 'events' : 'places';

      const count = eventsMode
        ? eventVisibleHandles.size
        : visibleHandles
          ? visibleHandles.size
          : cards.length;
      if (statusEl) statusEl.textContent = statusText(count);
      if (emptyEl) {
        const showEmpty = count === 0;
        emptyEl.hidden = !showEmpty;
        if (showEmpty) emptyEl.textContent = state.notFound ? messages.notFound || '' : messages.empty || '';
      }
      if (clearBtn) clearBtn.hidden = isDefault;
      if (locateBtn) locateBtn.disabled = eventsMode;
      if (mapShell) {
        mapShell.dataset.mapMode = eventsMode ? 'events' : 'physical';
        const mapEvents = mapShell.querySelector('[data-map-events]');
        if (mapEvents) mapEvents.hidden = !eventsMode;
      }

      // Preserve pin/card selection only while the selected card stays visible.
      if (eventsMode) selectedHandle = null;
      if (selectedHandle) {
        const selectedItem = itemByHandle.get(selectedHandle);
        if (!selectedItem || selectedItem.hidden) selectedHandle = null;
      }
      cards.forEach((card) => {
        const active = selectedHandle !== null && card.getAttribute('data-handle') === selectedHandle;
        card.classList.toggle('is-active', active);
        if (active) {
          card.setAttribute('aria-current', 'true');
        } else {
          card.removeAttribute('aria-current');
        }
      });

      // State contract with the deferred map module (assets/quas-stockist-map.js).
      root.dispatchEvent(
        new CustomEvent('quas-stockists:applied', {
          detail: {
            visibleHandles: eventsMode ? [] : visibleHandles ? Array.from(visibleHandles) : null,
            origin: eventsMode ? null : state.origin,
            selectedHandle,
            filter: state.filter,
            eventsMode,
          },
        })
      );
    };

    const applySelection = (handle, source) => {
      selectedHandle = handle || null;
      cards.forEach((card) => {
        const active = selectedHandle !== null && card.getAttribute('data-handle') === selectedHandle;
        card.classList.toggle('is-active', active);
        if (active) {
          card.setAttribute('aria-current', 'true');
        } else {
          card.removeAttribute('aria-current');
        }
      });
      if (selectedHandle && source === 'marker') {
        const item = itemByHandle.get(selectedHandle);
        if (item && item.classList.contains('is-beyond')) expandList();
        revealItem(item);
      }
    };

    root.addEventListener('quas-stockists:select', (event) => {
      const detail = event.detail || {};
      if (typeof detail.handle !== 'string') return;
      applySelection(detail.handle, detail.source === 'marker' ? 'marker' : 'card');
    });

    const setFilter = (value) => {
      state.filter = value;
      state.expanded = false;
      state.notFound = false;
      if (value === 'event_partner') state.origin = null;
      if (filtersWrap) {
        filtersWrap.querySelectorAll('[data-stockist-filter]').forEach((button) => {
          const active = button.getAttribute('data-stockist-filter') === value;
          button.classList.toggle('is-active', active);
          button.setAttribute('aria-pressed', active ? 'true' : 'false');
        });
      }
      apply();
    };

    const doClear = () => {
      searchSequence += 1;
      state.filter = 'all';
      state.query = '';
      state.origin = null;
      state.notFound = false;
      state.expanded = false;
      if (input) input.value = '';
      if (filtersWrap) {
        filtersWrap.querySelectorAll('[data-stockist-filter]').forEach((button) => {
          const active = button.getAttribute('data-stockist-filter') === 'all';
          button.classList.toggle('is-active', active);
          button.setAttribute('aria-pressed', active ? 'true' : 'false');
        });
      }
      apply();
    };

    const setOrigin = (origin) => {
      state.origin = origin;
      state.query = '';
      state.notFound = false;
      state.expanded = false;
      apply();
      if (resultsEl && listScrolls()) resultsEl.scrollTop = 0;
    };

    const lookupPostcode = (query, sequence) => {
      const compact = query.replace(/\s+/g, '').toUpperCase();
      const isFull = FULL_POSTCODE.test(query);
      const endpoint = isFull
        ? `https://api.postcodes.io/postcodes/${encodeURIComponent(compact)}`
        : `https://api.postcodes.io/outcodes/${encodeURIComponent(compact)}`;

      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);
      const pendingTimer = setTimeout(() => {
        if (searchForm) searchForm.classList.add('is-pending');
      }, PENDING_INDICATOR_MS);

      fetch(endpoint, { signal: controller.signal })
        .then((response) => {
          if (!response.ok) throw new Error('postcode lookup failed');
          return response.json();
        })
        .then((payload) => {
          if (sequence !== searchSequence) return;
          const result = payload && payload.result;
          if (!result || typeof result.latitude !== 'number' || typeof result.longitude !== 'number') {
            throw new Error('postcode lookup incomplete');
          }
          setOrigin({ lat: result.latitude, lng: result.longitude, label: result.postcode || result.outcode || compact });
        })
        .catch(() => {
          if (sequence !== searchSequence) return;
          state.origin = null;
          state.query = query;
          state.expanded = false;
          state.notFound = !venues.some(
            (venue) =>
              (state.filter === 'all' || venue.category === state.filter) && matchesQuery(venue, query)
          );
          apply();
        })
        .finally(() => {
          clearTimeout(timer);
          clearTimeout(pendingTimer);
          if (searchForm) searchForm.classList.remove('is-pending');
        });
    };

    if (searchForm && input) {
      searchForm.addEventListener('submit', (event) => {
        event.preventDefault();
        const query = input.value.trim();
        if (!query) {
          doClear();
          return;
        }
        hideAlert();
        searchSequence += 1;
        if (state.filter !== 'event_partner' && (FULL_POSTCODE.test(query) || OUTWARD_POSTCODE.test(query))) {
          lookupPostcode(query, searchSequence);
          return;
        }
        state.origin = null;
        state.query = query;
        state.expanded = false;
        const searchRecords = state.filter === 'event_partner' ? events : venues;
        state.notFound = !searchRecords.some(
          (venue) =>
            (state.filter === 'all' || venue.category === state.filter) && matchesQuery(venue, query)
        );
        apply();
      });
    }

    if (clearBtn) {
      clearBtn.addEventListener('click', () => {
        hideAlert();
        doClear();
      });
    }

    if (locateBtn) {
      locateBtn.addEventListener('click', () => {
        hideAlert();
        if (state.filter === 'event_partner') return;
        if (!navigator.geolocation) {
          showAlert(messages.locationError || '', locateBtn);
          return;
        }
        navigator.geolocation.getCurrentPosition(
          (position) => {
            setOrigin({ lat: position.coords.latitude, lng: position.coords.longitude, label: 'you' });
          },
          () => {
            showAlert(messages.locationError || '', locateBtn);
          },
          { timeout: LOCATE_TIMEOUT_MS, maximumAge: 300000 }
        );
      });
    }

    if (filtersWrap) {
      filtersWrap.querySelectorAll('[data-stockist-filter]').forEach((button) => {
        button.addEventListener('click', () => {
          hideAlert();
          setFilter(button.getAttribute('data-stockist-filter') || 'all');
        });
      });
    }

    root.querySelectorAll('[data-show-on-map]').forEach((button) => {
      button.addEventListener('click', () => {
        const handle = button.getAttribute('data-show-on-map');
        if (!handle) return;
        root.dispatchEvent(
          new CustomEvent('quas-stockists:select', { detail: { handle, source: 'card' } })
        );
        if (mapShell && !inViewport(mapShell)) scrollTo(mapShell);
      });
    });

    // The venue name is a pointer shortcut for the card's Show on map button,
    // live only once that button is revealed. Selecting text is left alone.
    listEl.addEventListener('click', (event) => {
      const name = event.target.closest('.quas-stockist-card__name');
      if (!name || String(window.getSelection()).trim() !== '') return;
      const button = name.closest('[data-quas-stockist]').querySelector('[data-show-on-map]');
      if (button && !button.hidden) button.click();
    });

    if (moreBtn) {
      moreBtn.addEventListener('click', () => {
        const firstHidden = listEl.querySelector('.quas-stockists__item.is-beyond [data-quas-stockist]');
        expandList();
        if (firstHidden) {
          firstHidden.setAttribute('tabindex', '-1');
          firstHidden.focus({ preventScroll: true });
        }
      });
    }

    // Reveal the JavaScript-only controls now that enhancement is live.
    if (searchForm) searchForm.hidden = false;
    if (filtersWrap) filtersWrap.hidden = false;

    // The deferred provider can initialise after the first applied event.
    // Replay current filters when it becomes ready so pins match the list.
    root.addEventListener('quas-stockist-map:ready', apply);

    // Category links reuse the same filter path as the visible controls.
    // An absent or unrecognised value preserves the ordinary full directory.
    const linkedCategory = new URLSearchParams(window.location.search).get('category');
    setFilter(LINKED_CATEGORIES.has(linkedCategory) ? linkedCategory : 'all');
  };

  document.querySelectorAll('[data-quas-stockists]').forEach(init);
})();
